import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { getSupabaseClient } from './supabase';
import { removeRecordLogo, uploadRecordLogo, type RecordLogoFields } from './recordLogos';
import { fetchEventTotalsBatched } from './eventTotals';

type ClientOption = { supabase?: SupabaseClient };

export type VenueSummary = {
  id: string;
  venue_name: string | null;
  city: string | null;
  state: string | null;
  address_line1?: string | null;
  postal_code?: string | null;
  website?: string | null;
  is_outdoor?: boolean | null;
  created_at?: string | null;
  event_count?: number;
  contracted_location_id?: string | null;
  ita_location_name?: string | null;
  ita_site_number?: number | null;
};

export type VenueDetailRow = {
  deleted_at?: string | null;
  merged_into_venue_id?: string | null;
  id: string;
  venue_name: string | null;
  city: string | null;
  state: string | null;
  is_outdoor: boolean | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  country: string | null;
  website: string | null;
  notes: string | null;
  total_meeting_space_sqft?: number | null;
  meeting_rooms_count?: number | null;
  inhouse_av_company?: string | null;
  inhouse_av_email?: string | null;
  inhouse_av_phone?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  is_contracted?: boolean | null;
  is_ita_internal?: boolean | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  contract_term_months?: number | null;
  contract_renewal_notice_days?: number | null;
  contract_file_path?: string | null;
  contracted_location_id?: string | null;
  ita_location_name?: string | null;
  ita_site_number?: number | null;
  logo_storage_path?: string | null;
  logo_public_url?: string | null;
  logo_alt_text?: string | null;
  logo_uploaded_at?: string | null;
  logo_uploaded_by?: string | null;
  /** R2 venue IDs. Only the merge read (`forMerge`) returns them. */
  r2_iids?: Array<number | string> | null;
};

export type VenueInsert = {
  venue_name: string;
  city?: string | null;
  state?: string | null;
  is_outdoor?: boolean | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  country?: string | null;
  website?: string | null;
  notes?: string | null;
};

const VENUE_DETAIL_SELECT =
  'id,venue_name,city,state,is_outdoor,address_line1,address_line2,postal_code,country,website,notes,total_meeting_space_sqft,meeting_rooms_count,inhouse_av_company,inhouse_av_email,inhouse_av_phone,created_at,created_by,updated_at,updated_by,is_contracted,is_ita_internal,contract_start_date,contract_end_date,contract_term_months,contract_renewal_notice_days,contract_file_path,contracted_location_id,logo_storage_path,logo_public_url,logo_alt_text,logo_uploaded_at,logo_uploaded_by' as const;

export type VenueLibrarySortKey = 'name' | 'address' | 'location' | 'type';
export type VenueLibrarySortDir = 'asc' | 'desc';

// Count on the server: fetching link rows truncates the entire page at the
// Data API row limit and undercounts both busy venues and their neighbors.
const VENUE_SUMMARY_SELECT = 'id,venue_name,city,state,address_line1,postal_code,website,is_outdoor,created_at,contracted_location_id,event_links:event_venues!event_venues_venue_id_fkey(count)';

function withVenueEventCounts(rows: (VenueSummary & { event_links: { count: number }[] })[]): VenueSummary[] {
  return rows.map(({ event_links, ...venue }) => ({
    ...venue,
    event_count: event_links[0]?.count ?? 0,
  }));
}

export async function fetchVenueLibrary(params: {
  search?: string;
  limit?: number;
  offset?: number;
  pageSize?: number;
  sortKey?: VenueLibrarySortKey;
  sortDir?: VenueLibrarySortDir;
  itaOnly?: boolean;
  recentOnly?: boolean;
  typeFilter?: 'all' | 'indoor' | 'outdoor';
  stateFilter?: string;
  supabase?: SupabaseClient;
}): Promise<{ data: VenueSummary[]; count: number | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const search = (params.search || '').trim();
  const effectivePageSize = params.pageSize ?? params.limit ?? 50;
  const effectiveOffset = params.offset ?? 0;
  const ascending = (params.sortDir ?? 'desc') === 'asc';
  const sortKey = params.sortKey ?? 'name';

  let query = supabase
    .from('venues')
    .select(VENUE_SUMMARY_SELECT, { count: 'exact' })
    .is('event_links.deleted_at', null)
    .is('deleted_at', null);

  if (search) {
    const escaped = search.replace(/"/g, '""');
    const quoted = `"%${escaped}%"`;
    query = query.or(
      `venue_name.ilike.${quoted},address_line1.ilike.${quoted},address_line2.ilike.${quoted},city.ilike.${quoted},state.ilike.${quoted},postal_code.ilike.${quoted}`,
    );
  }

  // Server-side ITA filter: collect all ITA venue IDs across the full dataset before pagination
  if (params.itaOnly) {
    const [{ data: lvRows }, { data: contractedRows }] = await Promise.all([
      supabase.from('location_venues').select('venue_id'),
      supabase.from('venues').select('id').not('contracted_location_id', 'is', null).is('deleted_at', null),
    ]);
    const itaIds = new Set<string>();
    for (const r of (lvRows || []) as { venue_id: string }[]) itaIds.add(r.venue_id);
    for (const r of (contractedRows || []) as { id: string }[]) itaIds.add(r.id);
    if (itaIds.size === 0) return { data: [], count: 0, error: null };
    query = query.in('id', Array.from(itaIds));
  }

  if (params.recentOnly) {
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    query = query.gte('created_at', cutoff);
  }

  if (params.typeFilter === 'indoor') {
    query = query.eq('is_outdoor', false);
  } else if (params.typeFilter === 'outdoor') {
    query = query.eq('is_outdoor', true);
  }

  const trimmedStateFilter = (params.stateFilter ?? '').trim().toUpperCase();
  if (trimmedStateFilter && trimmedStateFilter !== 'ALL') {
    query = query.eq('state', trimmedStateFilter);
  }

  switch (sortKey) {
    case 'name':
      query = query.order('venue_name', { ascending });
      break;
    case 'address':
      query = query.order('address_line1', { ascending });
      break;
    case 'location':
      query = query.order('city', { ascending }).order('state', { ascending });
      break;
    case 'type':
      query = query.order('is_outdoor', { ascending });
      break;
    default:
      query = query.order('venue_name', { ascending });
  }
  if (params.recentOnly) {
    // Keep "recently added" focused on newest entries first.
    query = query.order('created_at', { ascending: false });
  }
  query = query.order('id', { ascending: true });

  query = query.range(effectiveOffset, effectiveOffset + effectivePageSize - 1);

  const { data, error, count } = await query;
  if (error) return { data: [], count: null, error };

  const venues = withVenueEventCounts(data || []);
  const venueIds = venues.map(v => v.id).filter(Boolean);

  // Batch-fetch ITA location info: prefer contracted_location_id, fall back to location_venues
  if (venueIds.length > 0) {
    // Build venue → effective location_id map using contracted_location_id first
    const venueLocationMap = new Map<string, string>();
    for (const v of venues) {
      if (v.contracted_location_id) venueLocationMap.set(v.id, v.contracted_location_id);
    }

    // For any venues still without a location, check location_venues (is_default preferred)
    const venuesWithoutLocation = venues.filter(v => !venueLocationMap.has(v.id)).map(v => v.id);
    if (venuesWithoutLocation.length > 0) {
      const { data: lvRows } = await supabase
        .from('location_venues')
        .select('venue_id,location_id,is_default')
        .in('venue_id', venuesWithoutLocation);
      if (Array.isArray(lvRows)) {
        // Group by venue_id, prefer is_default=true, else take first
        const byVenue = new Map<string, { location_id: string; is_default: boolean }[]>();
        for (const r of lvRows as { venue_id: string; location_id: string; is_default: boolean }[]) {
          const arr = byVenue.get(r.venue_id) ?? [];
          arr.push(r);
          byVenue.set(r.venue_id, arr);
        }
        for (const [venueId, rows] of byVenue) {
          const preferred = rows.find(r => r.is_default) ?? rows[0];
          if (preferred) venueLocationMap.set(venueId, preferred.location_id);
        }
      }
    }

    const allLocationIds = Array.from(new Set(venueLocationMap.values()));
    if (allLocationIds.length > 0) {
      const [{ data: locRows }, { data: settingsRows }] = await Promise.all([
        supabase
          .from('locations')
          .select('location_id,name,display_name')
          .in('location_id', allLocationIds),
        supabase
          .from('location_settings')
          .select('location_id,site_number')
          .in('location_id', allLocationIds),
      ]);
      const nameMap = new Map<string, string>();
      for (const r of (locRows || []) as { location_id: string; name: string; display_name: string | null }[]) {
        nameMap.set(r.location_id, r.display_name || r.name);
      }
      const siteMap = new Map<string, number | null>();
      for (const r of (settingsRows || []) as { location_id: string; site_number: number | null }[]) {
        siteMap.set(r.location_id, r.site_number ?? null);
      }
      for (const v of venues) {
        const locId = venueLocationMap.get(v.id);
        if (locId) {
          v.ita_location_name = nameMap.get(locId) ?? null;
          v.ita_site_number = siteMap.get(locId) ?? null;
          // Ensure contracted_location_id is populated for the ITA filter even if it came from location_venues
          if (!v.contracted_location_id) v.contracted_location_id = locId;
        }
      }
    }
  }

  return { data: venues, count: count ?? null, error: null };
}

async function enrichFavoriteVenueSummaries(
  supabase: SupabaseClient,
  venues: VenueSummary[],
): Promise<void> {
  const venueIds = venues.map((venue) => venue.id).filter(Boolean);
  if (venueIds.length === 0) return;

  const venueLocationMap = new Map<string, string>();
  for (const venue of venues) {
    if (venue.contracted_location_id) venueLocationMap.set(venue.id, venue.contracted_location_id);
  }
  const venuesWithoutLocation = venues
    .filter((venue) => !venueLocationMap.has(venue.id))
    .map((venue) => venue.id);
  if (venuesWithoutLocation.length > 0) {
    const { data: venueLocationRows } = await supabase
      .from('location_venues')
      .select('venue_id,location_id,is_default')
      .in('venue_id', venuesWithoutLocation);
    if (Array.isArray(venueLocationRows)) {
      const rowsByVenue = new Map<string, { location_id: string; is_default: boolean }[]>();
      for (const row of venueLocationRows as { venue_id: string; location_id: string; is_default: boolean }[]) {
        const rows = rowsByVenue.get(row.venue_id) ?? [];
        rows.push(row);
        rowsByVenue.set(row.venue_id, rows);
      }
      for (const [venueId, rows] of rowsByVenue) {
        const preferred = rows.find((row) => row.is_default) ?? rows[0];
        if (preferred) venueLocationMap.set(venueId, preferred.location_id);
      }
    }
  }

  const locationIds = Array.from(new Set(venueLocationMap.values()));
  if (locationIds.length === 0) return;
  const [{ data: locationRows }, { data: settingsRows }] = await Promise.all([
    supabase
      .from('locations')
      .select('location_id,name,display_name')
      .in('location_id', locationIds),
    supabase
      .from('location_settings')
      .select('location_id,site_number')
      .in('location_id', locationIds),
  ]);
  const nameByLocation = new Map<string, string>();
  for (const row of (locationRows || []) as { location_id: string; name: string; display_name: string | null }[]) {
    nameByLocation.set(row.location_id, row.display_name || row.name);
  }
  const siteByLocation = new Map<string, number | null>();
  for (const row of (settingsRows || []) as { location_id: string; site_number: number | null }[]) {
    siteByLocation.set(row.location_id, row.site_number ?? null);
  }
  for (const venue of venues) {
    const locationId = venueLocationMap.get(venue.id);
    if (!locationId) continue;
    venue.ita_location_name = nameByLocation.get(locationId) ?? null;
    venue.ita_site_number = siteByLocation.get(locationId) ?? null;
    if (!venue.contracted_location_id) venue.contracted_location_id = locationId;
  }
}

export async function fetchFavoriteVenueSummaries(
  venueIds: string[],
  options?: ClientOption,
): Promise<{ data: VenueSummary[]; error: PostgrestError | null }> {
  const uniqueIds = Array.from(new Set(venueIds.filter(Boolean))).slice(0, 50);
  if (uniqueIds.length === 0) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('venues')
    .select(VENUE_SUMMARY_SELECT)
    .is('event_links.deleted_at', null)
    .in('id', uniqueIds)
    .is('deleted_at', null);
  if (error) return { data: [], error };
  const venues = withVenueEventCounts(data || []);
  await enrichFavoriteVenueSummaries(supabase, venues);
  const orderById = new Map(uniqueIds.map((id, index) => [id, index]));
  venues.sort((left, right) => (orderById.get(left.id) ?? 999) - (orderById.get(right.id) ?? 999));
  return { data: venues, error: null };
}

export async function fetchVenueDetail(
  venueId: string,
  options?: ClientOption & { forMerge?: boolean },
): Promise<{ data: VenueDetailRow | null; error: PostgrestError | null }> {
  if (!venueId) return { data: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  if (options?.forMerge) {
    const { data, error } = await supabase.rpc('read_venue_merge_candidate', { p_venue_id: venueId });
    return { data: data as VenueDetailRow | null, error };
  }
  const { data, error } = await supabase
    .from('venues')
    .select(VENUE_DETAIL_SELECT)
    .eq('id', venueId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) return { data: null, error };
  const venue = (data as VenueDetailRow) ?? null;
  if (venue) {
    // Resolve effective location: prefer contracted_location_id, fall back to location_venues
    let effectiveLocationId = venue.contracted_location_id ?? null;
    if (!effectiveLocationId) {
      const { data: lvRow } = await supabase
        .from('location_venues')
        .select('location_id,is_default')
        .eq('venue_id', venueId)
        .order('is_default', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (lvRow) effectiveLocationId = (lvRow as any).location_id ?? null;
    }
    // Keep detail pages aligned with the venue library: a default venue link is
    // the effective location until a contract-specific location is explicitly set.
    if (effectiveLocationId && !venue.contracted_location_id && !venue.is_ita_internal) {
      venue.contracted_location_id = effectiveLocationId;
    }
    if (effectiveLocationId) {
      const [{ data: locRow }, { data: settingsRow }] = await Promise.all([
        supabase
          .from('locations')
          .select('location_id,name,display_name')
          .eq('location_id', effectiveLocationId)
          .maybeSingle(),
        supabase
          .from('location_settings')
          .select('location_id,site_number')
          .eq('location_id', effectiveLocationId)
          .maybeSingle(),
      ]);
      if (locRow) {
        venue.ita_location_name = (locRow as any).display_name || (locRow as any).name || null;
      }
      if (settingsRow) {
        venue.ita_site_number = (settingsRow as any).site_number ?? null;
      }
    }
  }
  return { data: venue, error: null };
}

export async function createVenue(
  payload: VenueInsert,
  options?: ClientOption,
): Promise<{ data: VenueSummary | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const insert = {
    venue_name: payload.venue_name,
    city: payload.city ?? null,
    state: payload.state ?? null,
    is_outdoor: payload.is_outdoor ?? false,
    address_line1: payload.address_line1 ?? null,
    address_line2: payload.address_line2 ?? null,
    postal_code: payload.postal_code ?? null,
    country: payload.country ?? null,
    website: payload.website ?? null,
    notes: payload.notes ?? null,
  };
  const { data, error } = await supabase
    .from('venues')
    .insert(insert)
    .select('id,venue_name,city,state,is_outdoor,created_at')
    .maybeSingle();
  return { data: (data as VenueSummary) ?? null, error: error ?? null };
}

export async function updateVenueRecord(params: {
  venueId: string;
  patch: Partial<VenueDetailRow>;
  supabase?: SupabaseClient;
}): Promise<{ data: VenueDetailRow | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  if (!params.venueId) return { data: null, error: null };
  const { data, error } = await supabase
    .from('venues')
    .update(params.patch)
    .eq('id', params.venueId)
    .select(VENUE_DETAIL_SELECT)
    .maybeSingle();
  return { data: (data as VenueDetailRow) ?? null, error: error ?? null };
}

/** The venue's logo, like a customer's (recordLogos.ts), in the venue-logos bucket. */
export async function uploadVenueLogo(params: {
  venueId: string;
  venueName: string;
  file: File;
  previousStoragePath?: string | null;
  uploadedByProfileId?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  data: RecordLogoFields | null;
  error: StorageError | PostgrestError | Error | null;
  cleanupError: StorageError | null;
}> {
  return uploadRecordLogo({
    bucket: 'venue-logos',
    table: 'venues',
    recordId: params.venueId,
    displayName: params.venueName,
    fallbackName: 'Venue',
    file: params.file,
    previousStoragePath: params.previousStoragePath,
    uploadedByProfileId: params.uploadedByProfileId,
    supabase: params.supabase,
  });
}

export async function removeVenueLogo(params: {
  venueId: string;
  storagePath?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  error: StorageError | PostgrestError | null;
  cleanupError: StorageError | null;
}> {
  return removeRecordLogo({
    bucket: 'venue-logos',
    table: 'venues',
    recordId: params.venueId,
    storagePath: params.storagePath,
    supabase: params.supabase,
  });
}

export async function softDeleteVenue(
  venueId: string,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  if (!venueId) return null;
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('soft_delete', { table_name: 'venues', _id: venueId });
  return error ?? null;
}

export type VenueMergeCandidate = Pick<VenueDetailRow, 'id' | 'venue_name' | 'address_line1' | 'address_line2' | 'city' | 'state' | 'postal_code' | 'country' | 'deleted_at'>;

export async function fetchVenueMergeCandidates(search: string, includeArchived: boolean) {
  const { data, error } = await getSupabaseClient().rpc('search_venue_merge_candidates', {
    p_search: search, p_include_archived: includeArchived,
  });
  return { data: data as { venues: VenueMergeCandidate[]; truncated: boolean } | null, error };
}

export type VenueMergeInput = {
  sourceVenueId: string;
  targetVenueId: string;
  masterFields?: Record<string, { value: unknown; source: 'source' | 'target' }>;
};

export type VenueMergePreview = {
  source_archived_at?: string | null;
  preservation_rehearsal?: { rolled_back_verified: boolean; before_sha256: string; after_sha256: string };
  additional_venues: Array<{
    id: string;
    venue_name: string | null;
    address_line1: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    matches_name: boolean;
    matches_address: boolean;
  }>;
  venue_versions: Record<string, number>;
  final_identity: Record<string, string | null>;
};

function venueMergeArguments(params: VenueMergeInput) {
  const fields: Record<string, unknown> = {};
  for (const [key, { value, source }] of Object.entries(params.masterFields ?? {})) {
    fields[key] = value;
    fields[key + '_source'] = source;
  }
  return {
    p_source_venue_id: params.sourceVenueId,
    p_target_venue_id: params.targetVenueId,
    p_master_fields: fields,
  };
}

export async function previewVenueMerge(
  params: VenueMergeInput,
  options?: ClientOption,
): Promise<{ data: VenueMergePreview | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('preview_venue_merge', venueMergeArguments(params));
  return { data: data as VenueMergePreview | null, error };
}

export async function rehearseArchivedVenueMerge(
  params: VenueMergeInput & { expectedVersions: Record<string, number> },
): Promise<{ data: VenueMergePreview['preservation_rehearsal'] | null; error: PostgrestError | null }> {
  const { data, error } = await getSupabaseClient().rpc('rehearse_archived_venue_merge', {
    ...venueMergeArguments(params), p_additional_venue_ids: [], p_expected_versions: params.expectedVersions,
  });
  return { data, error };
}

export async function mergeVenues(
  params: VenueMergeInput & { additionalVenueIds: string[]; expectedVersions: Record<string, number> },
  options?: ClientOption,
): Promise<{
  data: { target_venue_id: string; venues_merged: number; events_updated: number; contacts_moved: number; rooms_moved: number } | null;
  error: PostgrestError | null;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('merge_venue_group', {
    ...venueMergeArguments(params),
    p_additional_venue_ids: params.additionalVenueIds,
    p_expected_versions: params.expectedVersions,
  });
  return { data, error };
}

export type VenueMergeImpact = { eventLinks: number; contacts: number; rooms: number };

/**
 * What a merge would move off one venue, for the merge dialog's pair card and review: its active event links,
 * contacts and rooms, each a head count on its partial `deleted_at is null` index. merge_venues also moves
 * soft-deleted rows, which these leave out on purpose.
 */
export async function fetchVenueMergeImpact(
  venueId: string,
  options?: ClientOption,
): Promise<{ data: VenueMergeImpact | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const count = (table: 'event_venues' | 'venue_contacts' | 'venue_rooms') => supabase.from(table)
    .select('id', { count: 'exact', head: true }).eq('venue_id', venueId).is('deleted_at', null);
  const [links, contacts, rooms] = await Promise.all([count('event_venues'), count('venue_contacts'), count('venue_rooms')]);
  const error = links.error ?? contacts.error ?? rooms.error ?? null;
  if (error) return { data: null, error };
  return { data: { eventLinks: links.count ?? 0, contacts: contacts.count ?? 0, rooms: rooms.count ?? 0 }, error: null };
}

export type VenueEventLink = {
  id: string;
  name: string | null;
  event_code?: string | null;
  r2_contract_iid?: string | null;
  client_id?: string | null;
  /** The linked customer's name (clients.business_name), from the server library page. */
  client_name?: string | null;
  client_business?: string | null;
  status?: string | null;
  show_start_date?: string | null;
  show_end_date?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  project_manager?: { full_name?: string | null; first_name?: string | null; last_name?: string | null } | null;
  net_event_amount?: number | null;
};

export async function fetchVenueEvents(
  venueId: string,
  options?: ClientOption,
): Promise<{ data: VenueEventLink[]; error: PostgrestError | null }> {
  if (!venueId) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_venues')
    .select(
      'id, deleted_at, events:events!event_venues_event_id_fkey(id,name,event_code,r2_contract_iid,client_business,status,show_start_date,show_end_date,event_start_date,event_end_date,deleted_at,project_manager:project_manager_profile_id(full_name,first_name,last_name))',
    )
    .eq('venue_id', venueId);
  if (error) return { data: [], error };
  const events = (data || [])
    .filter((row: any) => !row.deleted_at)
    .map((row: any) => row.events)
    .filter((ev: any) => ev && !ev.deleted_at)
    .map((ev: any) => ({
      id: String(ev.id),
      name: ev.name ?? null,
      event_code: ev.event_code ?? null,
      r2_contract_iid: ev.r2_contract_iid ?? null,
      client_business: ev.client_business ?? null,
      status: ev.status ?? null,
      show_start_date: ev.show_start_date ?? null,
      show_end_date: ev.show_end_date ?? null,
      event_start_date: ev.event_start_date ?? null,
      event_end_date: ev.event_end_date ?? null,
      project_manager: ev.project_manager ?? null,
    })) as VenueEventLink[];

  // Fetch totals for all linked events in one batch
  const eventIds = events.map(ev => ev.id);
  if (eventIds.length > 0) {
    const { data: totalsData } = await fetchEventTotalsBatched(supabase, eventIds);
    if (Array.isArray(totalsData)) {
      const totalsMap = new Map<string, number>();
      for (const row of totalsData) {
        const eid = row?.event_id;
        const rawAmount = row?.amount_post_discount;
        const amount = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount);
        if (typeof eid === 'string' && eid && Number.isFinite(amount)) {
          totalsMap.set(eid, amount);
        }
      }
      for (const ev of events) {
        ev.net_event_amount = totalsMap.get(ev.id) ?? null;
      }
    }
  }

  return { data: events, error: null };
}

export const venueLibraryQueryKey = (
  search: string,
  offset: number,
  pageSize: number,
  sortKey: VenueLibrarySortKey,
  sortDir: VenueLibrarySortDir,
  itaOnly: boolean,
  recentOnly: boolean,
  typeFilter: 'all' | 'indoor' | 'outdoor',
  stateFilter: string,
) => ['venues', 'library', search, offset, pageSize, sortKey, sortDir, itaOnly, recentOnly, typeFilter, stateFilter];

export const getVenueLibraryQueryOptions = ({
  search = '',
  limit,
  offset,
  pageSize,
  sortKey = 'name',
  sortDir = 'asc',
  itaOnly = false,
  recentOnly = false,
  typeFilter = 'all',
  stateFilter = 'all',
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  search?: string;
  limit?: number;
  offset?: number;
  pageSize?: number;
  sortKey?: VenueLibrarySortKey;
  sortDir?: VenueLibrarySortDir;
  itaOnly?: boolean;
  recentOnly?: boolean;
  typeFilter?: 'all' | 'indoor' | 'outdoor';
  stateFilter?: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => {
  const effectivePageSize = pageSize ?? limit ?? 50;
  const effectiveOffset = offset ?? 0;
  return {
    queryKey: venueLibraryQueryKey(search, effectiveOffset, effectivePageSize, sortKey, sortDir, itaOnly, recentOnly, typeFilter, stateFilter),
    enabled,
    queryFn: async () => {
      const { data, count, error } = await fetchVenueLibrary({
        search,
        limit,
        offset: effectiveOffset,
        pageSize: effectivePageSize,
        sortKey,
        sortDir,
        itaOnly,
        recentOnly,
        typeFilter,
        stateFilter,
        supabase,
      });
      if (error) throw error;
      return { data, count };
    },
    placeholderData: (previousData: { data: VenueSummary[]; count: number | null } | undefined) => previousData,
  };
};

export const venueDetailQueryKey = (venueId: string) => ['venue', venueId];

export const getVenueDetailQueryOptions = ({
  venueId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  venueId: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: venueDetailQueryKey(venueId),
  enabled: enabled && Boolean(venueId),
  queryFn: async () => {
    const { data, error } = await fetchVenueDetail(venueId, { supabase });
    if (error) throw error;
    return data;
  },
});

export const venueEventsQueryKey = (venueId: string) => ['venue', venueId, 'events'];

export const getVenueEventsQueryOptions = ({
  venueId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  venueId: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: venueEventsQueryKey(venueId),
  enabled: enabled && Boolean(venueId),
  queryFn: async () => {
    const { data, error } = await fetchVenueEvents(venueId, { supabase });
    if (error) throw error;
    return data;
  },
});

// ---------------------------------------------------------------------------
// Venue counts (events, contracts, total $) for RecordHeader badges
// ---------------------------------------------------------------------------

export type VenueCounts = {
  events: number;
  contracts: number;
  totalAmount: number;
};

export async function fetchVenueCounts(
  venueId: string,
  options?: ClientOption,
): Promise<{ data: VenueCounts; error: PostgrestError | null }> {
  if (!venueId) return { data: { events: 0, contracts: 0, totalAmount: 0 }, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();

  // Paginate over event_venues to correctly count venues with >1000 linked events.
  // Supabase defaults to a 1000-row cap; explicit .range() pages past it.
  const PAGE_SIZE = 1000;
  const allEvents: { id: string; status: string | null; r2_contract_iid: string | null }[] = [];
  let page = 0;
  while (true) {
    const { data: evRows, error: evError } = await supabase
      .from('event_venues')
      .select('events:events!event_venues_event_id_fkey(id,status,r2_contract_iid,deleted_at)')
      .eq('venue_id', venueId)
      .is('deleted_at', null)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (evError) return { data: { events: 0, contracts: 0, totalAmount: 0 }, error: evError };
    const pageEvents = ((evRows || []) as any[])
      .map((r: any) => r.events)
      .filter((ev: any) => ev && !ev.deleted_at);
    for (const ev of pageEvents) allEvents.push(ev);
    if (!evRows || evRows.length < PAGE_SIZE) break;
    page++;
  }

  const eventCount = allEvents.length;
  const contractCount = allEvents.filter(ev => ev.r2_contract_iid).length;

  // Only sum $ for invoiced events
  const invoicedIds = allEvents
    .filter(ev => ev.status === 'invoiced')
    .map(ev => String(ev.id));

  let totalAmount = 0;
  if (invoicedIds.length > 0) {
    const { data: totalsData } = await fetchEventTotalsBatched(supabase, invoicedIds);
    if (Array.isArray(totalsData)) {
      for (const row of totalsData) {
        const raw = row?.amount_post_discount;
        const amount = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(amount)) totalAmount += amount;
      }
    }
  }

  return {
    data: { events: eventCount, contracts: contractCount, totalAmount },
    error: null,
  };
}

export const venueCountsQueryKey = (venueId: string) => ['venue', venueId, 'counts'];

export const getVenueCountsQueryOptions = ({
  venueId,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  venueId: string;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => ({
  queryKey: venueCountsQueryKey(venueId),
  enabled: enabled && Boolean(venueId),
  queryFn: async () => {
    const { data, error } = await fetchVenueCounts(venueId, { supabase });
    if (error) throw error;
    return data;
  },
});

// ---------------------------------------------------------------------------
// Contract file helpers
// ---------------------------------------------------------------------------

export async function uploadVenueContractFile(params: {
  venueId: string;
  file: File;
  supabase?: SupabaseClient;
}): Promise<{ path: string | null; error: string | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const ext = params.file.name.split('.').pop() ?? 'pdf';
  const storagePath = `${params.venueId}/contract.${ext}`;

  const { error } = await supabase.storage
    .from('venue-contracts')
    .upload(storagePath, params.file, { cacheControl: '3600', upsert: true });

  if (error) return { path: null, error: error.message };
  return { path: storagePath, error: null };
}

export async function getVenueContractFileUrl(params: {
  filePath: string;
  supabase?: SupabaseClient;
}): Promise<string | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.storage
    .from('venue-contracts')
    .createSignedUrl(params.filePath, 3600);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

export async function deleteVenueContractFile(params: {
  filePath: string;
  supabase?: SupabaseClient;
}): Promise<string | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.storage
    .from('venue-contracts')
    .remove([params.filePath]);
  return error ? error.message : null;
}
